import { Group, Text, ActionIcon, Container, Card, Menu, TextInput, Button, Modal } from '@mantine/core';
import { IconClockHour4, IconSettings, IconDeviceDesktop, IconDeviceTablet, IconDeviceMobile, IconFlame, IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { snapdom } from '@zumer/snapdom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { useDocumentTitle } from '@mantine/hooks';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { HeaderMenu } from "../../components/HeaderMenu/HeaderMenu";
import { SelectTimePicker, useColorScheme, HelpTip } from "../../components/GeneralComponent/GeneralComponent";
import { visiUserId, visiUserToken } from '../../GeneralFunction';
import InsightsApi from "../../api/InsightsApi";
import { modals } from '@mantine/modals';
import { AssetsUrl } from "./AssetsUrl";
import './HeatmapView.css';

const loadResource = (tag, attributes) => {
  return new Promise((resolve, reject) => {
    const element = document.createElement(tag);
    Object.keys(attributes).forEach(key => element.setAttribute(key, attributes[key]));
    element.onload = resolve;
    element.onerror = reject;
    document.head.appendChild(element);
  });
};

export function HeatMapRecordingView() {
  useDocumentTitle("Visiopt - Insights");

  const navigate = useNavigate();
  const { HeatmapId } = useParams();
  const token = visiUserToken() ? visiUserToken() : '';

  const [iframeContent, setIframeContent] = useState('');
  const [loadResourceLoaded, setLoadResourceLoaded] = useState(0);
  const [deviceType, setDeviceType] = useState('0');
  const [heatmapDetails, setHeatmapDetails] = useState(false);
  const [createTime, setCreateTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [frontEndFramework, setFrontEndFramework] = useState('');
  const [PrerenderCodeStatus, setPrerenderCodeStatus] = useState('');
  const [heatmapHighAccuracy, setHeatmapHighAccuracy] = useState('');
  const [pageId, setPageId] = useState('');
  const [requestUrl, setRequestUrl] = useState('');
  const [testAssetsUrl, setTestAssetsUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isAssetsUrlModalOpen, setIsAssetsUrlModalOpen] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [selectedHeatmapName, setSelectedHeatmapName] = useState('');

  const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');
  const [selectedTimePicker, setSelectedTimePicker] = useState<number>();
  const [selectedTimePickerToDate, setSelectedTimePickerToDate] = useState('');
  const [selectedTimePickerFromDate, setSelectedTimePickerFromDate] = useState('');

  const [heatmapCreateDate, setHeatmapCreateDate] = useState('');
  // Top 10 Click Hotspots — ranked list posted up from the heatmap iframe.
  const [hotspots, setHotspots] = useState<any[]>([]);
  const [hotspotsOpen, setHotspotsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Export the FULL heatmap (page + heat + markers) as a PNG image. The heatmap lives in a
  // same-origin srcDoc iframe, so we can capture its whole document. snapdom renders via the
  // browser's own engine, so custom-font text kerns correctly (no overlapping words) and the
  // live layout is captured as-is (no re-flow / stretch).
  const handleExportHeatmap = async () => {
    const iframe = document.getElementById('vis') as HTMLIFrameElement | null;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc || !doc.body) {
      notifications.show({ color: 'red', title: 'Export', message: 'Heatmap is not ready yet. Please wait for it to load.' });
      return;
    }
    setExporting(true);
    handleShowHideLoader(true); // full-screen loader — the export can take several seconds
    try {
      const target = doc.body;
      const docEl = doc.documentElement;
      // Size the export to the HEAT LAYER itself, not the raw scrollWidth. The heat canvas is
      // only as wide as the on-screen view; scrollWidth can be wider (stray-right clicks and
      // clamped Top-10 pins push it out), which left a white "no heat" strip on the right.
      // Capturing to the heat canvas's own box keeps the image to exactly the heat-covered area.
      const heatCanvas = doc.querySelector('.visi-heatmap-canvas') as HTMLCanvasElement | null;
      const captureWidth = (heatCanvas && heatCanvas.offsetWidth)
        ? heatCanvas.offsetWidth
        : Math.max(docEl.clientWidth, target.clientWidth, docEl.scrollWidth);
      const captureHeight = (heatCanvas && heatCanvas.offsetHeight)
        ? heatCanvas.offsetHeight
        : Math.max(docEl.scrollHeight, target.scrollHeight, docEl.clientHeight);
      try { docEl.scrollTop = 0; target.scrollTop = 0; } catch (e) { /* ignore */ }
      // Let web fonts finish so the captured text uses the real font.
      try { if ((doc as any).fonts && (doc as any).fonts.ready) { await (doc as any).fonts.ready; } } catch (e) { /* ignore */ }
      // Resolution, bounded on THREE limits so the tab can't run out of memory (the live OOM):
      //  - desired 3x sharpness,
      //  - MAX_DIM: browsers cap a single image at ~16,384px per side,
      //  - MAX_PIXELS: a memory-safe TOTAL pixel budget (a very tall/wide page would otherwise
      //    produce a canvas too big to hold — this is what crashed the tab on live).
      // We also pin dpr:1 on snapdom below so a high-DPI monitor doesn't secretly multiply this.
      const MAX_DIM = 16000;
      // ~45 MP ceiling: high enough to keep the image sharp, but still a safe margin below the
      // ~57 MP that renders fine locally — so a very tall/wide page can't OOM the tab. The real
      // OOM cause was the DPR multiplier (fixed by dpr:1 on snapdom below), not this budget.
      const MAX_PIXELS = 45000000;
      let scale = Math.min(
        3,
        MAX_DIM / captureWidth,
        MAX_DIM / captureHeight,
        Math.sqrt(MAX_PIXELS / (captureWidth * captureHeight))
      );
      if (!isFinite(scale) || scale <= 0) { scale = 1; }
      const name = (selectedHeatmapName || 'heatmap').replace(/[^\w-]+/g, '_');

      // Work around `backdrop-filter` (frosted-glass panels). No DOM-to-image engine can render
      // it, so such a box comes out blank and its light text disappears. Temporarily disable the
      // filter and drop in a matching dark tint (only when the box has no solid background of its
      // own) so the text stays readable, capture, then restore the page exactly as it was.
      const bfRestores: Array<() => void> = [];
      try {
        const view = doc.defaultView;
        if (view) {
          doc.querySelectorAll('*').forEach((el) => {
            const cs = view.getComputedStyle(el as Element);
            const bf = (cs as any).backdropFilter || (cs as any).webkitBackdropFilter || 'none';
            if (bf && bf !== 'none') {
              const he = el as HTMLElement;
              const prevBf = he.style.backdropFilter;
              const prevWbf = (he.style as any).webkitBackdropFilter;
              const prevBg = he.style.background;
              he.style.backdropFilter = 'none';
              (he.style as any).webkitBackdropFilter = 'none';
              const bg = cs.backgroundColor;
              if (bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') {
                he.style.background = 'rgba(20, 20, 45, 0.38)';
              }
              bfRestores.push(() => {
                he.style.backdropFilter = prevBf;
                (he.style as any).webkitBackdropFilter = prevWbf;
                he.style.background = prevBg;
              });
            }
          });
        }
      } catch (e) { /* ignore */ }

      let dataUrl = '';
      try {
        // snapdom renders text through the browser's own engine (correct kerning, no overlap),
        // embeds fonts/images, and captures the heat <canvas> + numbered pins as-is. `clip` crops
        // to the heat region (no white strip); `scale` gives an HD image within the browser cap.
        const canvas = await snapdom.toCanvas(target, {
          scale: scale,
          dpr: 1, // don't let a high-DPI monitor multiply the pixel count (caused the live OOM)
          backgroundColor: '#ffffff',
          embedFonts: true,
          clip: { x: 0, y: 0, width: captureWidth, height: captureHeight },
        });
        dataUrl = canvas.toDataURL('image/png');
      } finally {
        // Always restore the page (undo the backdrop-filter workaround), even if capture threw.
        bfRestores.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
      }

      const link = document.createElement('a');
      link.download = `${name}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error('Heatmap export failed', e);
      notifications.show({ color: 'red', title: 'Export failed', message: 'Could not export the heatmap. Please try again.' });
    } finally {
      setExporting(false);
      handleShowHideLoader(false);
    }
  };
  let memberId = visiUserId() ? visiUserId() : '';
  const colorTheme = useColorScheme();

  // visiopt-visual.css, loaded for the preview, paints the body light grey and has no dark
  // version, so in the dark theme the report sat inside a light frame. Match the theme while
  // this page is open and restore the old value on the way out, so the other screens that
  // share that stylesheet are untouched. The phone/tablet frame gets a dark bezel the same way:
  // the device classes are toggled by jQuery further down, so this marks the preview itself
  // and the CSS only takes effect while a device frame is on.
  useEffect(() => {
    const frame = document.getElementById('vis');
    if (colorTheme === 'light') {
      if (frame) { frame.classList.remove('visi-frame-dark'); }
      return;
    }
    const previous = document.body.style.background;
    document.body.style.background = '#1A1B1E';
    if (frame) { frame.classList.add('visi-frame-dark'); }
    return () => { document.body.style.background = previous; };
  }, [colorTheme]);

  useEffect(() => {
    const loadResources = async () => {
      try {
        const frontendPublicUrl = process.env.REACT_APP_FRONTEND_PUBLIC_URL;
        await loadResource('script', { src: frontendPublicUrl+'js/yui-all.js' });       
        await loadResource('script', { src: frontendPublicUrl+'js/flobn-all.js' });
        await loadResource('script', { src: frontendPublicUrl+'js/jquery.min.js' });
        setLoadResourceLoaded(1);
      } catch (error) {
        console.error('Error loading resource:', error);
      }
    };
    if(HeatmapId !== ""){
      loadResources();
    }

  }, [HeatmapId]);

  // Receive the ranked Top 10 Click Hotspots from the heatmap iframe (same-origin
  // srcDoc). The iframe computes clusters over the plotted clicks and posts them here
  // so we can show the reference table above the heatmap.
  useEffect(() => {
    const onHotspots = (event: MessageEvent) => {
      const data = event?.data;
      if (data && data.type === 'visi_hotspots' && Array.isArray(data.hotspots)) {
        setHotspots(data.hotspots);
      }
    };
    window.addEventListener('message', onHotspots);
    return () => window.removeEventListener('message', onHotspots);
  }, []);

  const handleGetheatmapdetails = () => {
    const dataObject = {
      system_website_id: selectedMemberWebsite,
      heatmap_id: HeatmapId,
      time: selectedTimePicker,
      filter: '1',
      device_type: '0',
    } as any;
    if(selectedTimePicker === -2){
      const [fromMonth, fromDay, fromYear] = selectedTimePickerFromDate.split("-").map(num => parseInt(num, 10));
      const [endMonth, endDay, endYear] = selectedTimePickerToDate.split("-").map(num => parseInt(num, 10));
      const fromDate = `${fromMonth}/${fromDay}/${fromYear}`;
      const toDate = `${endMonth}/${endDay}/${endYear}`;
      dataObject.filter = '2';
      dataObject.from = fromDate;
      dataObject.to = toDate;
    }
    handleShowHideLoader(true);
    InsightsApi.getheatmapdetails(dataObject)
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
            handleShowHideLoader(false);
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          setSelectedHeatmapName(data.data.heatmap_name);
          setPageUrl(data.data.page_url);
          setCreateTime(data.data.create_time);
          setStartDate(data.data.start_date);
          setEndDate(data.data.end_date);
          setFrontEndFramework(data.data.front_end_framework);
          setPrerenderCodeStatus(data.data.prerender_code_status);
          setHeatmapHighAccuracy(data.data.heatmap_high_accuracy);
          setPageId(data.data.page_id);
          setRequestUrl(data.data.request_url);
          setTestAssetsUrl(data.data.test_assets_url);
          setWebsiteUrl(data.data.website_url);
          setHeatmapCreateDate(data.data.heatmap_created_date);
          setHeatmapDetails(true);
          handleShowHideLoader(false);
        }else{
          handleShowHideLoader(false);
        }
      })
      .catch(error => {
        console.error("API Error:", error);
        handleShowHideLoader(false);
      });
  };

  const handleClickHeatMap = async () => {
    handleShowHideLoader(true);
    let params = {
      page_url : pageUrl,
      heatmap_id : HeatmapId,
      token : token,
      system_website_id: selectedMemberWebsite,
      createTime: createTime,
      startDate: startDate,
      endDate: endDate,
      frontEndFramework: frontEndFramework,
      PrerenderCodeStatus: PrerenderCodeStatus,
      heatmapHighAccuracy: heatmapHighAccuracy,
      pageId: pageId,
      requestUrl: requestUrl,
      testAssetsUrl: testAssetsUrl,
      websiteUrl: websiteUrl,
      deviceType: deviceType,
      selectedTime: selectedTimePicker,
      member_id: memberId,
    };
    let paramsJSON = JSON.stringify(params);
    let paramsJSONEncoded = btoa(paramsJSON);

  try {
    const response = await axios.get(
      process.env.REACT_APP_BACKEND_API_PATH+"heatmapsetup-data.php",
      {
        params: {
          heatmapParams: paramsJSONEncoded,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    setIframeContent(response.data);
    handleShowHideLoader(false);
  } catch (error) {
    handleShowHideLoader(false);
    console.error('Error:', error);
  }
  };

  const handleHeaderMenuComponentChange = (Value:any) => {
    setSelectedMemberWebsite(Value);
  };

  const getStartDate = () => {
    const [year, month, day] = heatmapCreateDate.split('-');
    return `${year}-${month}-${day}`;
  };

  const getSelectedFromDate = () => {
    if(selectedTimePickerFromDate){
      const [month, day, year] = selectedTimePickerFromDate.split('-');
      return `${year}-${month}-${day}`;
    }else{
      const [year, month, day] = heatmapCreateDate.split('-');
      return `${year}-${month}-${day}`;
    }
  };

  const getSelectedToDate = () => {
    if(selectedTimePickerToDate){
      const [month, day, year] = selectedTimePickerToDate.split('-');
      return `${year}-${month}-${day}`;
    }else{
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  };

  const handleselectedTimePickerComponentChange = (Value:any) => {
    setSelectedTimePicker(Value);
  };

  const handleselectedTimePickerToDateChange = (Value:any) => {
    setSelectedTimePickerToDate(Value);
  };

  const handleselectedTimePickerFromDateChange = (Value:any) => {
    setSelectedTimePickerFromDate(Value);
  };

  const handleMobileView = () => {
    setDeviceType('1');
    const testmobileFunction = (window as any).testmobile;
    if (typeof testmobileFunction === 'function') {
      testmobileFunction();
    };
  };

  const handlePCView = () => {
    setDeviceType('0');
    const testPcFunction = (window as any).testpc;
    if (typeof testPcFunction === 'function') {
      testPcFunction();
    };
  };

  const handleTabletView = () => {
    setDeviceType('2');
    const testTabletFunction = (window as any).testtablet;
    if (typeof testTabletFunction === 'function') {
      testTabletFunction();
    };
  };

  const handleAssetsUrlModalOpen = () => {
    setIsAssetsUrlModalOpen(true);
  };

  const handleAssetsUrlModalClose = () => {
    setIsAssetsUrlModalOpen(false);
  };

  const handleBackToInsightsClick = () => {
    navigate("/heat-maps");
  };

  useEffect(() => {
    if(HeatmapId !== '' && deviceType && selectedMemberWebsite && selectedTimePicker){
      handleGetheatmapdetails();
    }
  }, [HeatmapId, deviceType, selectedMemberWebsite, selectedTimePicker]);


  useEffect(() => {
    if(heatmapDetails) {
      handleClickHeatMap();
      setHeatmapDetails(false);
    }
  }, [heatmapDetails]);

  const handleResetStatClick = () => modals.openConfirmModal({
    title: 'Please confirm your action',
    children: (
      <Text size="sm">
        Are you sure you want to reset this report? You will lose any current report data.
      </Text>
    ),
    labels: { confirm: 'Confirm', cancel: 'Cancel' },
    onCancel: () => {console.log('Cancel')},
    onConfirm: () => {handleResetData()},
  });

  const handleResetData = () => {
    handleShowHideLoader(true);
    InsightsApi.resetheatmapdata({
      heatmap_id: HeatmapId
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
            handleShowHideLoader(false);
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          handleGetheatmapdetails();
          handleShowHideLoader(false);
        }else{
          handleShowHideLoader(false);
        }
      })
      .catch(error => {
        console.error("API Error:", error);
        handleShowHideLoader(false);
      });
  };


  const handleShowHideLoader = (value:any) => {
    var overlay = window.parent.parent.document.getElementById('overlay');
    if(value === true){
      overlay.innerHTML = '<div id="small_overlay" style="visibility: visible; display: block; background: none;"><h1>Page Loading...</h1><img src="/img/loading.gif" height="22" width="126" alt="Loading..."></div>';
      overlay.style.display = 'block';
      overlay.style.visibility = 'visible';
    }else{
      overlay.style.display = 'none';
      overlay.style.visibility = 'hidden';
    }
  };

  return (
    <>
      <HeaderMenu HeaderMenuValue={selectedMemberWebsite} HeaderMenuOnChange={handleHeaderMenuComponentChange}/>
      {/*START ASSETS URL*/}
      <AssetsUrl
        isAssetsUrlModalOpen={isAssetsUrlModalOpen}
        setIsAssetsUrlModalOpen={setIsAssetsUrlModalOpen}
        handleAssetsUrlModalClose={handleAssetsUrlModalClose}
        assetsUrlValue={testAssetsUrl}
        setAssetsUrlValue={setTestAssetsUrl}
        pageId={pageId}
        assetsredirecturl={handleGetheatmapdetails}
      />
      {/*END ASSETS URL*/}  
      <div style={{ height: '100vh' }}>
        <Container size="xxl">
          <Card shadow="sm" padding="sm" radius="md" withBorder style={{ height: '100%' }}>
            <div style={{ display: 'flex',marginBottom: '10px' }}>
              <TextInput id="heatmap_created_date" value={createTime} style={{ display: 'none' }} readOnly />
              <TextInput id="get_time_selected" value={selectedTimePicker} style={{ display: 'none' }} readOnly />
              <TextInput id="from" value={selectedTimePickerFromDate} style={{ display: 'none' }} readOnly />
              <TextInput id="to" value={selectedTimePickerToDate} style={{ display: 'none' }} readOnly />
              <Group position="apart" mb="xs">
                <Text weight={500}>{selectedHeatmapName} <HelpTip helpId={252} /></Text>
              </Group>
              <div style={{ display: 'flex',marginLeft:"auto"}}>
              <ActionIcon
                  variant="outline"
                  size="lg"
                  title="Desktop"
                  onClick={handlePCView}
                  style={{ backgroundColor: deviceType === '0' ? '#e7fcf5' : 'transparent'}}
                >
                      <IconDeviceDesktop style={{ width: '25px', height: '30px' }} />
                </ActionIcon>
                <ActionIcon
                  variant="outline"
                  size="lg"
                  title="Tablet"
                  onClick={handleTabletView}
                  style={{ margin: '0px 10px', backgroundColor: deviceType === '2' ? '#e7fcf5' : 'transparent'}}
                >
                      <IconDeviceTablet style={{ transform: 'rotate(90deg)', width: '25px', height: '30px' }}/>
                </ActionIcon>
                <ActionIcon
                  variant="outline"
                  size="lg"
                  title="Mobile"
                  onClick={handleMobileView}
                  style={{ backgroundColor: deviceType === '1' ? '#e7fcf5' : 'transparent'}}
                >
                    <IconDeviceMobile style={{ width: '25px', height: '30px' }} />
                </ActionIcon>
                {/*<div rel="devicechange" className="test-pc-active" id="test-pc" onClick={handlePCView} title="Desktop"></div>
                <div rel="devicechange" className="test-tablet" id="test-tablet" onClick={handleTabletView} title="Tablet"></div>
                <div rel="devicechange" className="test-mobile" id="test-mobile" onClick={handleMobileView} title="Mobile"></div>*/}
                <ActionIcon
                    variant="white"
                    size="lg"
                    ml={10}
                    title="Time Filter"
                >
                  <IconClockHour4 size="1.1rem" />
                </ActionIcon>
                {/*used "reviewing_test" in the SelectedPage because the condition for reviewing_test already exists in the General Components*/}
                <SelectTimePicker  SelectTimePickerValue={selectedTimePicker} SelectTimePickerOnChange={handleselectedTimePickerComponentChange} SelectTimePickerFromDateOnChange={handleselectedTimePickerFromDateChange} SelectTimePickerToDateOnChange={handleselectedTimePickerToDateChange} SelectTestStartDate={getSelectedFromDate()} SelectTestEndDate={getSelectedToDate()} SelectStartDate={getStartDate()} SelectedPage='reviewing_test'/>
                <Menu width={260} position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      sx={(theme) => ({
                        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : "#fff",
                      })}
                      title="Settings"
                      ml={5}
                    >
                      <IconSettings size="1.1rem" />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item px={10} py={6} id="heatmap_refresh_stats" >Refresh Stats</Menu.Item>
                    <Menu.Item px={10} py={6} onClick={handleResetStatClick}>Reset Stats</Menu.Item>
                    <Menu.Item px={10} py={6} onClick={handleAssetsUrlModalOpen}>Image/Style Not Loaded</Menu.Item>
                  </Menu.Dropdown>
                </Menu>
                {hotspots.length > 0 && (
                  <Button
                    leftIcon={<IconFlame size="1rem" />}
                    variant={colorTheme === "light" ? "light" : "outline"}
                    color="orange"
                    style={{ marginLeft: '5px' }}
                    onClick={() => setHotspotsOpen(true)}
                  >
                    Top 10 Hotspots
                  </Button>
                )}
                <Button
                  leftIcon={<IconDownload size="1rem" />}
                  variant={colorTheme === "light" ? "default" : "outline"}
                  style={{ marginLeft: '5px' }}
                  loading={exporting}
                  onClick={handleExportHeatmap}
                  title="Download heatmap as image"
                >
                  Export Image
                </Button>
                <Button variant={colorTheme === "light" ? "default" : "outline"} style={{ marginLeft: '5px' }} onClick={handleBackToInsightsClick}>Back to Insights</Button>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 18px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Text weight={600}>Page URL:</Text>
                <Link to={pageUrl} target="_blank" title={pageUrl} style={{ textDecoration: 'none', color: colorTheme === "light" ? 'black' : 'white', maxWidth: '45vw', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pageUrl}</Link>
              </div>
              {/* Which device's clicks are currently shown */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 10px', borderRadius: '14px', fontWeight: 600, background: colorTheme === "light" ? '#e7f5ff' : '#1f3a4d', color: colorTheme === "light" ? '#1864ab' : '#a5d8ff' }}>
                {deviceType === '0' ? <IconDeviceDesktop size={16} /> : deviceType === '2' ? <IconDeviceTablet size={16} /> : <IconDeviceMobile size={16} />}
                <span>{deviceType === '0' ? 'Desktop' : deviceType === '2' ? 'Tablet' : 'Mobile'} clicks</span>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <Text weight={600}>Total Clicks:</Text>
                <Text id={'heatmap_total_click'}></Text>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <Text weight={600}>Total Visits:</Text>
                <Text id={'heatmap_total_visits'}></Text>
              </div>
            </div>
            <div className="test-wrapper flexed" style={{ width: '100%', height: '100%' }}>
              <Helmet>
                <link rel="stylesheet" type="text/css" href="/css/visiopt-visual.css"/>
                {loadResourceLoaded === 1 ?
                <script type="text/javascript">
                {`
                  var jQcustom = jQuery.noConflict();
                  function adjustIframeSize() {
                    var offset = jQcustom('.header-element').outerHeight() || 230;
                    var window_height = jQcustom(window).height() - offset;
                    var window_width = jQcustom(window).width();

                    jQcustom('#vis').css({
                      "height": window_height + "px",
                      "width": window_width + "px !important"
                    });
                  }

                  adjustIframeSize();

                  jQcustom(window).resize(function() {
                    adjustIframeSize();
                  });

                  function testmobile(){
                    jQcustom('#test-pc').removeClass('test-pc-active');
                    jQcustom('#test-tablet').removeClass('test-tablet-active');
                    jQcustom('#test-mobile').addClass('test-mobile-active');
                    jQcustom('#test-pc').addClass('test-pc');
                    jQcustom('#test-tablet').addClass('test-tablet');
                    jQcustom('#test-mobile').removeClass('test-mobile');
                    jQcustom('#vis').addClass('active_visi_mobile');
                    jQcustom('#vis').removeClass('active_visi_tablet');
                  }
                  function testpc(){
                    jQcustom('#test-pc').removeClass('test-pc');
                    jQcustom('#test-mobile').addClass('test-mobile');
                    jQcustom('#test-tablet').addClass('test-tablet');
                    jQcustom('#test-pc').addClass('test-pc-active');
                    jQcustom('#test-mobile').removeClass('test-mobile-active');
                    jQcustom('#test-tablet').removeClass('test-tablet-active');
                    jQcustom('#vis').removeClass('active_visi_mobile');
                    jQcustom('#vis').removeClass('active_visi_tablet');
                  }
                  function testtablet(){
                    jQcustom('#test-pc').addClass('test-pc');
                    jQcustom('#test-mobile').addClass('test-mobile');
                    jQcustom('#test-tablet').removeClass('test-tablet');
                    jQcustom('#test-pc').removeClass('test-pc-active');
                    jQcustom('#test-mobile').removeClass('test-mobile-active');
                    jQcustom('#test-tablet').addClass('test-tablet-active');
                    jQcustom('#vis').removeClass('active_visi_mobile');
                    jQcustom('#vis').addClass('active_visi_tablet');
                  }
                `}
                </script>
                : ''}
              </Helmet>
              <link rel="stylesheet" id="test_added_font" type="text/css" />
              <style type="text/css" id="test_added_css"></style>
              <iframe srcDoc={iframeContent} style={{ height: "100vh", border: 'none', userSelect: 'none', display: 'block', marginLeft: deviceType === '0' ? '-15px' : undefined, marginRight: deviceType === '0' ? '0' : undefined, marginTop: deviceType === '0' ? '-5px' : undefined }} id="vis" title="HeatMapIframe">
              </iframe>
            </div>
          </Card>
        </Container>
      </div>
      <div id="overlay" style={{ visibility: "hidden", display: "none", backgroundImage: "none", backgroundColor: 'rgba(255, 255, 255, 0.85555)' }} />
      <Modal opened={hotspotsOpen} onClose={() => setHotspotsOpen(false)} title={<Text weight={600}>Top 10 Click Hotspots</Text>} size="lg" centered>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '14px', width: '100%' }}>
            <thead>
              <tr>
                {['Rank', 'Color', 'Element / Section', 'Clicks', '% of Page Clicks', 'Type'].map((head) => (
                  <th key={head} style={{ textAlign: 'left', padding: '8px 14px', borderBottom: `2px solid ${colorTheme === 'light' ? '#dee2e6' : '#495057'}`, fontWeight: 600 }}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hotspots.map((h) => (
                <tr key={h.rank}>
                  <td style={{ padding: '8px 14px', borderBottom: `1px solid ${colorTheme === 'light' ? '#f1f3f5' : '#343a40'}`, fontWeight: 700 }}>#{h.rank}</td>
                  <td style={{ padding: '8px 14px', borderBottom: `1px solid ${colorTheme === 'light' ? '#f1f3f5' : '#343a40'}` }}>
                    <span style={{ display: 'inline-block', width: '18px', height: '18px', borderRadius: '4px', background: h.color, verticalAlign: 'middle' }} />
                  </td>
                  <td style={{ padding: '8px 14px', borderBottom: `1px solid ${colorTheme === 'light' ? '#f1f3f5' : '#343a40'}`, maxWidth: '260px' }} title={h.label || ''}>{h.label || 'Page area'}</td>
                  <td style={{ padding: '8px 14px', borderBottom: `1px solid ${colorTheme === 'light' ? '#f1f3f5' : '#343a40'}` }}>{Number(h.clicks).toLocaleString()}</td>
                  <td style={{ padding: '8px 14px', borderBottom: `1px solid ${colorTheme === 'light' ? '#f1f3f5' : '#343a40'}` }}>{h.percent}%</td>
                  <td style={{ padding: '8px 14px', borderBottom: `1px solid ${colorTheme === 'light' ? '#f1f3f5' : '#343a40'}` }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, background: h.clickable ? '#e6f4ea' : '#f1f3f5', color: h.clickable ? '#1e7e34' : '#6c757d' }}>
                      {h.clickable ? 'Clickable' : 'Non-clickable'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
}